import { getReasonPhrase } from 'http-status-codes'
import { z } from 'zod'
import { parseDataOfType } from '../common/parseDataType'
import * as SentryClient from '../sentryClient'

export const apiErrorSchema = z.object({
  error: z.object({
    kind: z.string(),
    message: z.string()
  })
})
export type ApiError = z.infer<typeof apiErrorSchema>

type ApiErrorHandlerOptions = {
  report?: boolean
}

const apiResponseErrors = new WeakSet<Error>()

export function isApiResponseError(error: unknown): error is Error {
  return error instanceof Error && apiResponseErrors.has(error)
}

function shouldReport(res: Response, options: ApiErrorHandlerOptions): boolean {
  return options.report ?? (res.status !== 401 && res.status !== 403)
}

function responseError(message: string): Error {
  const error = new Error(message)
  apiResponseErrors.add(error)
  return error
}

function reportResponseError(error: Error, res: Response, options: ApiErrorHandlerOptions): void {
  if (!shouldReport(res, options)) return
  SentryClient.captureException(error, {
    tags: { status_code: res.status },
    extra: {
      status: res.status,
      statusText: res.statusText,
      url: res.url
    }
  })
}

export async function apiErrorHandler(res: Response, options: ApiErrorHandlerOptions = {}): Promise<void> {
  if (!res.ok) {
    let apiError: ApiError
    let jsonData: unknown
    try {
      jsonData = await res.json()
      apiError = parseDataOfType(jsonData, apiErrorSchema)
    } catch {
      // instances when the backend does not return a json object
      const fallbackMessage = typeof jsonData === 'string' ? jsonData : getReasonPhrase(res.status)
      const fallbackError = responseError(fallbackMessage)
      reportResponseError(fallbackError, res, options)
      throw fallbackError
    }
    const newApiError = responseError(apiError.error.message)
    reportResponseError(newApiError, res, options)
    throw newApiError
  }
}
